import { chatFiles, isQueuedMessage, type Chat, type ChatCreateBody, type ChatKind, type Message, type WsEvent } from '@jerome2/shared';
import { api, qs } from '../api.ts';
import { createStore } from '../lib/store.ts';
import { retitleChatTabs } from './tabs.ts';

// Gesprekken en berichten, met streaming tekst via de websocket. Een bericht dat binnenkomt terwijl Jerome
// bezig is, wacht op de server (content.queued) tot zijn beurt klaar is; Stop en het kruisje halen het weg en
// geven de tekst terug, zodat het scherm die in het vak kan zetten. Opgepakt krijgt het een nieuw volgnummer.
// Wat Stop of het kruisje weghaalde, komt niet terug als wachtend bericht: een antwoord op het versturen, een
// melding of een lijst die nog onderweg was, kan het anders als spookbericht terugzetten.
// Een gesprek dat buiten een beurt om verandert (de titel uit het model na een geslaagde beurt, met de kosten daarvan) komt als
// chat.updated binnen: de lijst en de tabbladen van dat gesprek tonen de nieuwe titel meteen, zonder herladen.
// Eén regel voor de status en de titel van een gesprek en voor elk bericht: de nieuwste wint. Een antwoord van de server (de lijst van
// gesprekken, één gesprek, aanmaken, wijzigen, versturen, de lijst van berichten) is een momentopname van het begin van het verzoek.
// Wat de store daarna vastlegde, met een nummer van één oplopende klok (`statusMark` geeft het nummer van vóór een verzoek), is nieuwer
// en blijft; de rest komt van het antwoord. Vastgelegd worden de status (chat.status, en wat de client zelf zet bij versturen en Stop;
// ticket 28405), de titel (chat.updated, de titel uit het model; ticket 29165) en elk bericht (chat.message, chat.delta en het antwoord
// op versturen; 2.2.47, en dat antwoord alleen als de server het bericht sinds het versturen nog niet meldde, anders zette het een
// bericht dat Jerome al had opgepakt, terug op "In de wachtrij"; 2.2.59). Voor een bericht geldt dat ook als de oudere lijst het niet of
// in een oudere versie noemt: de server meldt elke wijziging aan een bericht, dus de store loopt daarna niet achter, en het nummer
// zegt wat de store al wist, niet wat de lijst weet.
// Zonder die regel bleef een gesprek dat al klaar was op bezig staan, zette een lijst die nog onderweg was de eerste regel van de vraag
// terug als titel, en verdween een antwoord van Jerome, of stond het leeg, tot herladen. Wat de store al vóór het verzoek had, volgt
// het antwoord: een bericht dat dan niet in de lijst staat, gaat weg, want dan is de lijst de waarheid; een weggehaald wachtend bericht
// blijft weg (zie hierboven). Van twee lijsten voor hetzelfde gesprek geldt de laatst gevraagde: elk verzoek om een lijst krijgt een
// eigen nummer, en een lijst die vóór de laatst overgenomen lijst werd gevraagd en pas daarna binnenkomt, verandert niets meer, ook
// niet aan een bericht dat alleen de nieuwere had (2.2.48). Twee aanvullingen. Het antwoord op hernoemen met de hand geldt altijd, en
// is zelf de nieuwste titel. En een pad van een bestand in OneDrive verdwijnt nooit (2.2.46): de server haalt een pad niet weg, dus een
// bekend pad blijft staan als een ouder antwoord (de lijst, het antwoord op versturen, een melding) hetzelfde bestand in hetzelfde
// bericht zonder pad noemt, en een pad dat de server meegeeft, wint; anders verdween de link "In OneDrive" na een late kopie (2.2.45)
// weer, tot herladen.

export interface ChatState {
  chats: Record<string, Chat>;
  messages: Record<string, Message[]>;
  loaded: boolean;
  /** Vluchtig: de chattabbladen waarvan het mailpaneel naast het gesprek de gesprekkenkolom van de schil laat wijken (desktop,
   * onder 1240 px); de schil toont dan de strook. Met twee panelen kunnen het er twee zijn. */
  listSuppressed?: string[];
}

export const chatStore = createStore<ChatState>({ chats: {}, messages: {}, loaded: false });

/** Zet (zolang het mailpaneel van dit chattabblad de ruimte nodig heeft) of wist de vraag om de gesprekkenkolom te laten wijken. */
export function suppressChatList(tabId: string, on: boolean): void {
  chatStore.set((s) => {
    const list = s.listSuppressed ?? [];
    if (list.includes(tabId) === on) return s;
    return { ...s, listSuppressed: on ? [...list, tabId] : list.filter((id) => id !== tabId) };
  });
}

/** Berichten die Stop of het kruisje uit de wachtrij haalde, de laatste vijftig (de oudste eerst). */
const removed = new Set<string>();
const REMOVED_KEPT = 50;

/** Een wachtend bericht dat al is weggehaald (een antwoord of melding die achterliep). */
const removedWhileQueued = (m: Message): boolean => isQueuedMessage(m) && removed.has(m.id);

/** Het bericht `next` met het pad in OneDrive dat de store al kende (`prev`: hetzelfde bericht zoals de store het nu heeft) bij elk
 * bestand dat in `next` geen pad heeft; een pad in `next` wint (zie de kop van dit bestand). Valt er niets aan te vullen, dan komt
 * `next` zelf terug. */
function keepFilePaths(next: Message, prev: Message | undefined): Message {
  const files = next.content['files'];
  if (!prev || !Array.isArray(files)) return next;
  const known = new Map(chatFiles(prev).flatMap((f) => (f.path ? [[f.id, f.path] as const] : [])));
  if (!known.size) return next;
  let kept = false;
  const merged = files.map((f: unknown) => {
    if (!f || typeof f !== 'object') return f;
    const file = f as Record<string, unknown>;
    const path = typeof file['id'] === 'string' ? known.get(file['id']) : undefined;
    if (!path || (typeof file['path'] === 'string' && file['path'])) return f;
    kept = true;
    return { ...file, path };
  });
  return kept ? { ...next, content: { ...next.content, files: merged } } : next;
}

/** Alleen voor tests: vergeet welke berichten zijn weggehaald. */
export function resetRemovedForTests(): void {
  removed.clear();
}

/** Teller van wat de client vastlegt (een status, een titel of een bericht): elke vastlegging krijgt een hoger nummer. */
let noteClock = 0;
/** Per gesprek (bij berichten: per bericht) de laatst vastgelegde waarde met haar nummer, ook voor een gesprek dat de store nog niet
 * kent (de melding kan vóór het antwoord op het aanmaken komen); per soort de laatste tweehonderd. */
type Notes<T> = Map<string, { value: T; at: number }>;
const NOTES_KEPT = 200;
/** De status: chat.status, en wat de client zelf zet bij versturen en Stop. */
const statuses: Notes<Chat['status']> = new Map();
/** De titel: chat.updated (de titel uit het model) en het antwoord op hernoemen met de hand. */
const titles: Notes<string> = new Map();
/** De berichten die de store kreeg of bijwerkte, via `upsertMessage` (chat.message en het antwoord op versturen) of een chat.delta,
 * met hun gesprek: een lijst die vóór dat nummer werd gevraagd, kent die versie nog niet, en die versie wint (zie de kop van dit
 * bestand). */
const messageNotes: Notes<string> = new Map();
/** Per gesprek het nummer van het verzoek van de nieuwste lijst die de store overnam: een lijst die daarvóór werd gevraagd en later
 * binnenkomt, is ouder dan wat de store al weet en verandert niets (zie de kop van dit bestand). */
const listMarks = new Map<string, number>();

/** Het nummer vóór een verzoek dat een gesprek teruggeeft: een status of titel die daarna is vastgelegd, is nieuwer dan het antwoord.
 * `loadMessages` neemt voor de lijst van berichten een eigen nummer uit dezelfde klok. */
export function statusMark(): number {
  return noteClock;
}

function note<T>(notes: Notes<T>, key: string, value: T): void {
  notes.delete(key);
  notes.set(key, { value, at: ++noteClock });
  if (notes.size > NOTES_KEPT) {
    const oldest = notes.keys().next();
    if (!oldest.done) notes.delete(oldest.value);
  }
}

/** De waarde die nieuwer is dan het antwoord op een verzoek van `since`, of null: zoals de store haar nu kent (`known`), of de
 * vastgelegde als de store het gesprek nog niet kent. */
function newerThan<T>(notes: Notes<T>, chatId: string, since: number, known: T | undefined): T | null {
  const seen = notes.get(chatId);
  return seen && seen.at > since ? (known ?? seen.value) : null;
}

function noteStatus(chatId: string, status: Chat['status']): void {
  note(statuses, chatId, status);
}

/** Een titel die nieuwer is dan elk antwoord op een verzoek van daarvoor: uit chat.updated, of het antwoord op hernoemen met de hand. */
function noteTitle(chatId: string, title: string): void {
  note(titles, chatId, title);
}

/** Een nieuwe status van een gesprek (chat.status, of Stop zonder lopende beurt): vastgelegd, ook als de store het gesprek nog niet kent. */
function setStatus(chatId: string, status: Chat['status']): void {
  noteStatus(chatId, status);
  chatStore.set((s) => {
    const c = s.chats[chatId];
    return c ? { ...s, chats: { ...s.chats, [chatId]: { ...c, status } } } : s;
  });
}

/** Of de store dit bericht na het begin van een verzoek van `since` kreeg of bijwerkte (een melding van de server, of het antwoord op
 * versturen): die versie is nieuwer dan het antwoord op dat verzoek (zie de kop van dit bestand). */
function newerMessage(id: string, since: number): boolean {
  return (messageNotes.get(id)?.at ?? 0) > since;
}

/** De status die nieuwer is dan het antwoord op een verzoek van `since`, of null: zoals de store hem nu kent (`known`), of de
 * vastgelegde als de store het gesprek nog niet kent. */
function newerStatus(chatId: string, since: number, known: Chat | undefined): Chat['status'] | null {
  return newerThan(statuses, chatId, since, known?.status);
}

/** De titel die nieuwer is dan het antwoord op een verzoek van `since`, of null: zoals de store hem nu kent (`known`), of de
 * vastgelegde als de store het gesprek nog niet kent. */
function newerTitle(chatId: string, since: number, known: Chat | undefined): string | null {
  return newerThan(titles, chatId, since, known?.title);
}

/** Het gesprek uit een antwoord op een verzoek van `since`, met de nieuwere status als die er is. */
function withNewestStatus(chat: Chat, since: number, known: Chat | undefined): Chat {
  const status = newerStatus(chat.id, since, known);
  return status && status !== chat.status ? { ...chat, status } : chat;
}

/** Het gesprek uit een antwoord op een verzoek van `since`, met de nieuwere titel als die er is. */
function withNewestTitle(chat: Chat, since: number, known: Chat | undefined): Chat {
  const title = newerTitle(chat.id, since, known);
  return title !== null && title !== chat.title ? { ...chat, title } : chat;
}

/** Zet een gesprek uit een antwoord op een verzoek van `since` in de store, met de nieuwste status en, behalve bij `ownTitle` (het
 * antwoord op hernoemen met de hand), de nieuwste titel. */
function putChat(chat: Chat, since: number, ownTitle = false): Chat {
  const known = chatStore.get().chats[chat.id];
  const withStatus = withNewestStatus(chat, since, known);
  const next = ownTitle ? withStatus : withNewestTitle(withStatus, since, known);
  chatStore.set((s) => ({ ...s, chats: { ...s.chats, [next.id]: next } }));
  return next;
}

export async function loadChats(kind?: ChatKind): Promise<Chat[]> {
  const since = statusMark();
  const r = await api<{ chats: Chat[] }>(`/api/chats${qs({ kind })}`);
  const known = chatStore.get().chats;
  const list = r.chats.map((c) => withNewestTitle(withNewestStatus(c, since, known[c.id]), since, known[c.id]));
  chatStore.set((s) => {
    const chats = { ...s.chats };
    for (const c of list) chats[c.id] = c;
    return { ...s, chats, loaded: true };
  });
  return list;
}

/** Eén gesprek, bijvoorbeeld het briefinggesprek dat niet in de lijst van de chat staat (andere soort). */
export async function loadChat(chatId: string): Promise<Chat> {
  const since = statusMark();
  const chat = await api<Chat>(`/api/chats/${encodeURIComponent(chatId)}`);
  return putChat(chat, since);
}

export async function loadMessages(chatId: string): Promise<Message[]> {
  // Een eigen nummer uit dezelfde klok: van twee lijsten voor hetzelfde gesprek is de laatst gevraagde de nieuwere, ook als ze vlak na
  // elkaar werden gevraagd.
  const since = ++noteClock;
  const r = await api<{ messages: Message[] }>(`/api/chats/${encodeURIComponent(chatId)}/messages`);
  // Een lijst die vóór de laatst overgenomen lijst van dit gesprek werd gevraagd, is ouder dan wat de store al weet: ze verandert niets.
  if (since < (listMarks.get(chatId) ?? 0)) return chatStore.get().messages[chatId] ?? [];
  listMarks.set(chatId, since);
  // Wat de store na het begin van het verzoek kreeg of bijwerkte, is nieuwer dan de lijst en wint, of het er nu in staat of niet; de rest
  // volgt de lijst, met het pad in OneDrive dat de store al kende (zie de kop van dit bestand). Een weggehaald bericht staat niet meer in
  // de store, en de lijst laat een weggehaald wachtend bericht weg.
  const current = chatStore.get().messages[chatId] ?? [];
  const known = new Map(current.map((m) => [m.id, m]));
  const newer = current.filter((m) => newerMessage(m.id, since));
  const newerIds = new Set(newer.map((m) => m.id));
  const listed = r.messages.filter((m) => !removedWhileQueued(m) && !newerIds.has(m.id)).map((m) => keepFilePaths(m, known.get(m.id)));
  const messages = newer.length ? [...listed, ...newer].sort((a, b) => a.seq - b.seq) : listed;
  chatStore.set((s) => ({ ...s, messages: { ...s.messages, [chatId]: messages } }));
  return messages;
}

/** Een formulier met de tekst en de bestanden van een bericht (zie server/src/chat/routes.ts). */
function messageForm(text: string, files: readonly File[], extra: Record<string, string> = {}): FormData {
  const form = new FormData();
  form.set('text', text);
  for (const [k, v] of Object.entries(extra)) form.set(k, v);
  for (const f of files) form.append('files', f, f.name);
  return form;
}

/** Maakt een gesprek, met bestanden als formulier: soort en tekst, en de titel en de context (als JSON) als die er zijn, zoals bij
 * een gesprek over een gevlagde mail dat met een bestand begint (ticket 30961). */
export async function createChat(body: Partial<ChatCreateBody> & { kind: ChatKind }, files: readonly File[] = []): Promise<Chat> {
  const since = statusMark();
  const extra = { kind: body.kind, ...(body.title ? { title: body.title } : {}), ...(body.context ? { context: JSON.stringify(body.context) } : {}) };
  const chat = files.length ? await api<Chat>('/api/chats', { method: 'POST', form: messageForm(body.text ?? '', files, extra) }) : await api<Chat>('/api/chats', { method: 'POST', body });
  return putChat(chat, since);
}

/** Verstuurt een bericht (met bestanden als formulier) en geeft het terug (met zijn volgnummer, zodat het antwoord erna te herkennen is). */
/** `opts.eventId` (sinds 2.2.54, Bespreek bij een afspraak): het agenda-item waar het bericht over gaat, als gegevens naast de tekst. */
export async function sendMessage(chatId: string, text: string, files: readonly File[] = [], opts: { eventId?: string } = {}): Promise<Message> {
  const since = statusMark();
  const path = `/api/chats/${encodeURIComponent(chatId)}/messages`;
  const m = files.length ? await api<Message>(path, { method: 'POST', form: messageForm(text, files) }) : await api<Message>(path, { method: 'POST', body: opts.eventId ? { text, eventId: opts.eventId } : { text } });
  noteSent(m, since);
  return m;
}

/** Een verstuurd bericht komt in de store en het gesprek staat op bezig (`since`: `statusMark()` van vóór het versturen; `chat`: het
 * gesprek zoals de server het meegaf, bij ontwikkelen en regels). Haalden Stop of het kruisje het bericht al weg voordat dit antwoord
 * binnen was, dan komt het niet terug; kwam er intussen een nieuwere status binnen (de beurt was al klaar), of was het bericht weg,
 * dan blijft de status zoals de server hem intussen meldde. Een titel die intussen binnenkwam, blijft ook, en het bericht zelf ook: meldde
 * de server het al (in de wachtrij, of al opgepakt), dan is die versie even nieuw of nieuwer dan dit antwoord (2.2.59). */
export function noteSent(m: Message, since: number, chat?: Chat): void {
  const gone = removed.has(m.id);
  // De nieuwste wint ook hier: het antwoord is een momentopname van toen het bericht binnenkwam, en een melding van daarna (Jerome pakte
  // het wachtende bericht op) zet het anders terug op "In de wachtrij", tot herladen.
  if (!newerMessage(m.id, since)) upsertMessage(m);
  const known = chatStore.get().chats[m.chatId];
  const newer = newerStatus(m.chatId, since, known);
  // Bezig is dan zelf de nieuwste status: een lijst die vóór het versturen werd gevraagd, zet het gesprek niet terug op rust.
  if (!gone && !newer) noteStatus(m.chatId, 'running');
  const c = chat ?? known;
  if (!c) return;
  const status = newer ?? (gone ? (known?.status ?? c.status) : 'running');
  const title = newerTitle(m.chatId, since, known) ?? c.title;
  chatStore.set((s) => ({ ...s, chats: { ...s.chats, [c.id]: { ...c, status, title } } }));
}

/** De gesprekken waarvoor deze client Stop vroeg (ticket 30961, deel 2): het einde van die beurt geeft geen stip, ook niet als Jerome
 * al tekst had geschreven. state/chatTurns.ts haalt een gesprek eruit zodra de beurt eindigt of een nieuwe begint. */
export const stopRequests = new Set<string>();

/** Stopt de beurt van Jerome; de wachtende berichten gaan weg en komen terug, zodat het scherm de tekst in het vak kan zetten. */
export async function stopChat(chatId: string): Promise<{ stopped: boolean; dequeued: Message[] }> {
  stopRequests.add(chatId);
  const r = await api<{ stopped: boolean; dequeued: Message[] }>(`/api/chats/${encodeURIComponent(chatId)}/stop`, { method: 'POST' }).catch((e: unknown) => {
    stopRequests.delete(chatId);
    throw e;
  });
  for (const m of r.dequeued) removeMessage(chatId, m.id);
  // Er liep geen beurt: staat het gesprek hier nog op bezig (de server was herstart, of de melding dat Jerome klaar was, ging
  // verloren), dan gaat het op rust, als nieuwste status: een lijst die nog onderweg was, zet het niet terug. Anders bleef Stop staan.
  if (!r.stopped && chatStore.get().chats[chatId]?.status === 'running') setStatus(chatId, 'idle');
  return r;
}

/** Haalt één wachtend bericht uit de wachtrij (het kruisje); de server weigert als Jerome het al heeft opgepakt. */
export async function removeQueuedMessage(chatId: string, messageId: string): Promise<Message> {
  const r = await api<{ message: Message }>(`/api/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}`, { method: 'DELETE' });
  removeMessage(chatId, messageId);
  return r.message;
}

export function removeMessage(chatId: string, messageId: string): void {
  // Ook onthouden als het bericht hier (nog) niet staat: het antwoord op het versturen kan nog onderweg zijn.
  removed.delete(messageId);
  removed.add(messageId);
  if (removed.size > REMOVED_KEPT) {
    const oldest = removed.values().next();
    if (!oldest.done) removed.delete(oldest.value);
  }
  chatStore.set((s) => {
    const list = s.messages[chatId];
    if (!list?.some((m) => m.id === messageId)) return s;
    return { ...s, messages: { ...s.messages, [chatId]: list.filter((m) => m.id !== messageId) } };
  });
}

export async function patchChat(chatId: string, patch: Partial<Pick<Chat, 'title' | 'modelTier' | 'fast' | 'pinned' | 'archived'>>): Promise<Chat> {
  const since = statusMark();
  const chat = await api<Chat>(`/api/chats/${encodeURIComponent(chatId)}`, { method: 'PATCH', body: patch });
  // Hernoemen met de hand: de titel uit het antwoord geldt, ook als er tijdens het verzoek een titel uit het model binnenkwam (die
  // raakt een titel met de hand nooit meer aan), en is de nieuwste, zodat een lijst die nog onderweg was hem niet terugzet.
  // Vastzetten, archiveren en de rest veranderen de titel niet: daar wint de nieuwste titel, zoals bij elk ander antwoord.
  const renamed = patch.title !== undefined;
  const next = putChat(chat, since, renamed);
  if (renamed) noteTitle(next.id, next.title);
  return next;
}

export async function deleteChat(chatId: string): Promise<void> {
  await api(`/api/chats/${encodeURIComponent(chatId)}`, { method: 'DELETE' });
  chatStore.set((s) => {
    const chats = { ...s.chats };
    delete chats[chatId];
    const messages = { ...s.messages };
    delete messages[chatId];
    return { ...s, chats, messages };
  });
}

export function upsertMessage(m: Message): void {
  if (removedWhileQueued(m)) return;
  // Met een nummer vastgelegd: een lijst die hiervóór werd gevraagd, haalt dit bericht niet weg en zet het niet terug (zie de kop).
  note(messageNotes, m.id, m.chatId);
  chatStore.set((s) => {
    const list = s.messages[m.chatId] ?? [];
    const prev = list.find((x) => x.id === m.id);
    // Een pad in OneDrive dat de store al kende, blijft (zie de kop van dit bestand).
    const kept = keepFilePaths(m, prev);
    // Ook bij vervangen sorteren: een bericht dat uit de wachtrij is opgepakt, krijgt een nieuw volgnummer.
    const next = (prev ? list.map((x) => (x.id === m.id ? kept : x)) : [...list, kept]).sort((a, b) => a.seq - b.seq);
    return { ...s, messages: { ...s.messages, [m.chatId]: next } };
  });
}

export function applyChatEvent(ev: WsEvent): void {
  if (ev.type === 'chat.message') upsertMessage(ev.message);
  else if (ev.type === 'chat.message.removed') removeMessage(ev.chatId, ev.messageId);
  else if (ev.type === 'chat.delta') {
    // Een stukje tekst werkt het bericht ook bij: een lijst die hiervóór werd gevraagd, zet de tekst niet terug (zie de kop).
    note(messageNotes, ev.messageId, ev.chatId);
    chatStore.set((s) => {
      const list = s.messages[ev.chatId];
      if (!list) return s;
      const idx = list.findIndex((x) => x.id === ev.messageId);
      if (idx < 0) return s;
      const m = list[idx]!;
      const next = [...list];
      next[idx] = { ...m, content: { ...m.content, text: `${String(m.content['text'] ?? '')}${ev.text}` } };
      return { ...s, messages: { ...s.messages, [ev.chatId]: next } };
    });
  } else if (ev.type === 'chat.status') setStatus(ev.chatId, ev.status);
  else if (ev.type === 'chat.updated') {
    // Het gesprek zoals de server het meldt, behalve de status: die komt alleen met chat.status, en die melding kan op deze voorlopen
    // (de volgende beurt begon al terwijl het model de titel maakte). De titel is vanaf nu de nieuwste (ticket 29165): een antwoord op
    // een verzoek dat hiervóór begon (de lijst, één gesprek, aanmaken, versturen, vastzetten) houdt hem en neemt de rest over; alleen
    // het antwoord op hernoemen met de hand geldt, want de titel uit het model raakt een titel met de hand nooit aan.
    noteTitle(ev.chat.id, ev.chat.title);
    chatStore.set((s) => {
      const known = s.chats[ev.chat.id];
      return { ...s, chats: { ...s.chats, [ev.chat.id]: known ? { ...ev.chat, status: known.status } : ev.chat } };
    });
    retitleChatTabs(ev.chat);
  }
}

export function sortedChats(state: Pick<ChatState, 'chats'>, kind: ChatKind, opts: { archived?: boolean } = {}): Chat[] {
  return Object.values(state.chats)
    .filter((c) => c.kind === kind && c.archived === Boolean(opts.archived))
    .sort((a, b) => Number(b.pinned) - Number(a.pinned) || (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt));
}
